import { useDroppable } from '@dnd-kit/core'
import { useEffect, useRef } from 'react'

import { SkeletonRows } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'
import {
  type SalesLeadFilters,
  type SalesLeadListItem,
  type SalesLeadStatus,
  salesLeadStatusColors,
  salesLeadStatusLabels,
  useInfiniteSalesLeads,
} from '@/models/salesLead'
import { DraggablePipelineCard } from './PipelineCard'

interface PipelineColumnProps {
  status: SalesLeadStatus
  count: number
  // Shared board filters (search, rep, state, …) minus status.
  filters: Omit<SalesLeadFilters, 'status' | 'page'>
}

export function PipelineColumn({ status, count, filters }: PipelineColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: status })
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteSalesLeads({
    ...filters,
    status,
  })

  // Read "is fetching" through a ref so the observer isn't torn down + rebuilt on
  // every page fetch (which could cascade-load all pages while the sentinel stays
  // in view).
  const fetchingRef = useRef(isFetchingNextPage)
  fetchingRef.current = isFetchingNextPage

  const sentinelRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const node = sentinelRef.current
    if (!node || !hasNextPage) return
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && !fetchingRef.current) {
        void fetchNextPage()
      }
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasNextPage, fetchNextPage])

  const leads: SalesLeadListItem[] = data?.pages.flatMap((p) => p.results) ?? []

  return (
    <div className="bg-surface flex h-full min-h-0 w-72 shrink-0 flex-col rounded-lg border border-neutral-200">
      {/* Sticky header: stays put while the card list below scrolls. */}
      <div className="flex items-center justify-between border-b border-neutral-200 px-3 py-2">
        <span className={cn('rounded px-1.5 py-0.5 text-xs font-medium', salesLeadStatusColors[status])}>
          {salesLeadStatusLabels[status]}
        </span>
        <span className="text-muted-foreground text-xs tabular-nums">{count}</span>
      </div>

      <div
        ref={setNodeRef}
        className={cn('flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-2', isOver && 'bg-blue-50')}
      >
        {isLoading ? (
          <SkeletonRows rows={4} rowClassName="h-20" />
        ) : (
          leads.map((lead) => <DraggablePipelineCard key={lead.id} lead={lead} />)
        )}

        {hasNextPage && (
          <div ref={sentinelRef} className="flex justify-center py-2">
            {isFetchingNextPage && <Spinner className="size-4" />}
          </div>
        )}
      </div>
    </div>
  )
}
